import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const Thumb = ({ id, onOpen, onDelete }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/screenshots/${id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); });
    return () => u && URL.revokeObjectURL(u);
  }, [id]);
  return (
    <div className="relative group aspect-video rounded-md overflow-hidden border border-slate-800 bg-slate-900" data-testid={`screenshot-thumb-${id}`}>
      {url ? <img src={url} alt="Trade chart" className="w-full h-full object-cover cursor-zoom-in" onClick={() => onOpen(url)} /> : <div className="w-full h-full animate-pulse" />}
      <button onClick={() => onDelete(id)} data-testid={`delete-screenshot-${id}`} className="absolute top-1.5 right-1.5 p-1.5 rounded bg-black/70 text-slate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
};

export const ScreenshotsDialog = ({ trade, onClose, onChanged }) => {
  const input = useRef(null);
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => { setIds(trade?.screenshots || []); setPreview(null); }, [trade]);

  const upload = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { const { data } = await api.post(`/trades/${trade.id}/screenshots`, fd); setIds((x) => [...x, data.id]); toast.success("Screenshot attached"); }
      catch (err) { toast.error(errMsg(err)); }
    }
    setBusy(false);
    onChanged();
  };

  const remove = async (id) => {
    try { await api.delete(`/trades/${trade.id}/screenshots/${id}`); setIds((x) => x.filter((i) => i !== id)); onChanged(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Dialog open={!!trade} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121722] border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="screenshots-dialog">
        <DialogHeader><DialogTitle className="font-display">{trade?.symbol} · chart screenshots</DialogTitle></DialogHeader>
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Trade chart full" className="w-full rounded-md border border-slate-800" data-testid="screenshot-preview" />
            <button onClick={() => setPreview(null)} data-testid="close-preview-button" className="absolute top-2 right-2 p-1.5 rounded bg-black/70 text-slate-200"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ids.map((id) => <Thumb key={id} id={id} onOpen={setPreview} onDelete={remove} />)}
            </div>
            {!ids.length && <p className="text-sm text-slate-500" data-testid="no-screenshots">No screenshots yet. Attach your setup chart to review later.</p>}
            <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple hidden onChange={upload} data-testid="screenshot-file-input" />
            <Button onClick={() => input.current.click()} disabled={busy} data-testid="upload-screenshot-button" className="bg-emerald-600 hover:bg-emerald-500 text-white w-fit">
              <ImagePlus className="h-4 w-4 mr-1" />{busy ? "Uploading…" : "Attach screenshot"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
